import { useState, useEffect, useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AppContext from '../../../context';
import Data from '../../../repository';
import Loading from '../../components/Loading';
import styles from '../../../styles/Services.module.scss';
import moment from 'moment';

function MyServiceBookings() {
	const context = useContext(AppContext);
	const [searchParams] = useSearchParams();
	const [bookings, setBookings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [verifying, setVerifying] = useState(false);
	const [paymentFailed, setPaymentFailed] = useState(false);

	useEffect(() => {
		fetchBookings();
	}, []);

	useEffect(() => {
		const success = searchParams.get('success') === 'true';
		if (!success) return;

		const checkoutId = searchParams.get('checkoutId') || localStorage.getItem('pendingServiceBulkCheckoutId');
		if (!checkoutId) return;

		let cancelled = false;
		const verify = async () => {
			try {
				setVerifying(true);
				const result = await Data.verifySumupBulkServiceBookings(checkoutId);
				if (!cancelled && result.status === 200) {
					localStorage.removeItem('pendingServiceBulkCheckoutId');
					await fetchBookings();
					setPaymentFailed(false);
				}
			} catch (err) {
				if (!cancelled && err.response?.status === 402) {
					setPaymentFailed(true);
					localStorage.removeItem('pendingServiceBulkCheckoutId');
				}
			} finally {
				if (!cancelled) setVerifying(false);
			}
		};

		verify();
		return () => {
			cancelled = true;
		};
	}, [searchParams]);

	const fetchBookings = async () => {
		try {
			const res = await Data.fetchMyServiceBookings();
			setBookings(res.data);
		} catch (error) {
			console.error('Error fetching bookings:', error);
		} finally {
			setLoading(false);
		}
	};

	const getStatusClass = (status) => {
		switch (status) {
			case 'confirmed': return styles.confirmed;
			case 'pending': return styles.pending;
			case 'cancelled': return styles.cancelled;
			case 'completed': return styles.completed;
			default: return '';
		}
	};

	// Separate upcoming and past bookings
	const today = moment().startOf('day');
	const upcomingBookings = bookings.filter(b => moment(b.date).isSameOrAfter(today) && b.status !== 'cancelled');
	const pastBookings = bookings.filter(b => moment(b.date).isBefore(today) || b.status === 'cancelled');

	if (loading) return <Loading text="Loading your bookings..." />;

	return (
		<div className={styles.myBookings}>
			<h1>My Service Bookings</h1>
			{verifying && <p style={{ color: '#888', marginBottom: '16px' }}>Confirming your payment...</p>}
			{paymentFailed && <p style={{ color: 'red', marginBottom: '16px' }}>Payment failed. Please try again.</p>}

			{bookings.length === 0 ? (
				<div className={styles.emptyState}>
					<h3>No bookings yet</h3>
					<p>You haven't made any service bookings.</p>
					<Link to="/dashboard/services">Browse Services</Link>
				</div>
			) : (
				<>
					{upcomingBookings.length > 0 && (
						<>
							<h2 style={{ fontSize: '18px', marginBottom: '16px', color: '#666' }}>Upcoming</h2>
							<div className={styles.bookingsList}>
								{upcomingBookings.map(booking => (
									<Link
										key={booking._id}
										to={`/dashboard/service-bookings/${booking._id}`}
										style={{ textDecoration: 'none', color: 'inherit' }}
									>
										<div className={styles.bookingCard}>
											<div className={styles.top}>
												<div>
													<h3>{booking.Service?.name}</h3>
													<span className={styles.staff}>
														with {booking.Staff?.firstName} {booking.Staff?.lastName}
													</span>
												</div>
												<span className={`${styles.status} ${getStatusClass(booking.status)}`}>
													{booking.status}
												</span>
											</div>
											<div className={styles.dateTime}>
												<span>📅 {moment(booking.date).format('ddd, MMM D, YYYY')}</span>
												<span>🕐 {booking.startTime} - {booking.endTime}</span>
											</div>
										</div>
									</Link>
								))}
							</div>
						</>
					)}

					{pastBookings.length > 0 && (
						<>
							<h2 style={{ fontSize: '18px', marginTop: '32px', marginBottom: '16px', color: '#666' }}>
								Past & Cancelled
							</h2>
							<div className={styles.bookingsList}>
								{pastBookings.map(booking => (
									<Link
										key={booking._id}
										to={`/dashboard/service-bookings/${booking._id}`}
										style={{ textDecoration: 'none', color: 'inherit' }}
									>
										<div className={styles.bookingCard} style={{ opacity: 0.7 }}>
											<div className={styles.top}>
												<div>
													<h3>{booking.Service?.name}</h3>
													<span className={styles.staff}>
														with {booking.Staff?.firstName} {booking.Staff?.lastName}
													</span>
												</div>
												<span className={`${styles.status} ${getStatusClass(booking.status)}`}>
													{booking.status}
												</span>
											</div>
											<div className={styles.dateTime}>
												<span>📅 {moment(booking.date).format('ddd, MMM D, YYYY')}</span>
												<span>🕐 {booking.startTime} - {booking.endTime}</span>
											</div>
										</div>
									</Link>
								))}
							</div>
						</>
					)}
				</>
			)}

			<div style={{ marginTop: '32px', textAlign: 'center' }}>
				<Link to="/dashboard/services" className={styles.bookButton} style={{ display: 'inline-block', textDecoration: 'none' }}>
					Book New Service
				</Link>
			</div>
		</div>
	);
}

export default MyServiceBookings;
